import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AppConstants } from '@app/@core/entities/app-constants';
import { AlertService } from '@app/@core/services/alert.service';
import { AttachmentsService } from '@app/@core/services/attachments.service';
import { AuthService } from '@app/@core/services/auth.service';
import { HttpService } from '@app/@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { SocketAdapterService } from '@app/@core/services/socket-adapter.server';
import { ChatService } from '@app/pages/chat/@core/services/chat.service';
import { DocumentUploadComponent } from '@app/pages/chat/document-upload/document-upload.component';
import { UserService } from '@app/pages/rbac/users/user.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import * as moment from 'moment';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs';

declare const $: any;
const DATE_FORMAT = 'DD MMMM YYYY';
@Component({
  standalone: false,
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, AfterViewInit {
  @ViewChild('onTyping', { read: ElementRef, static: false }) onTyping: ElementRef;
  @ViewChild('uploadDocument', { static: false }) private uploadDocument;
  alertService: AlertService;
  attachmentService: AttachmentsService;
  authService: AuthService;
  case_id: number;
  chatForm: UntypedFormGroup;
  chatService: ChatService;
  currentUser: any;
  document_category: string;
  document_status: string;
  document_title: string;
  document_type: string;
  documents_remarks: string;
  filteredUserList: any[] = [];
  httpService: HttpService;
  isReceiverTyping: boolean;
  isTyping: boolean;
  is_fileupload: boolean;
  is_fileshare: boolean;
  last_message_id: any;
  localService: LocalService;
  newChatUser: any;
  onTypingSubs: Subscription;
  onlineUsers: any[] = [];
  orgId: number;
  orgSubs: Subscription;
  person_id: number;
  selectedDocumentId: string;
  selectedUserAbbrevation: string;
  selectedUserChat: any[] = [];
  selectedUserId: number;
  selectedUserName: string;
  selectedUserRole: string;
  socketAdapter: SocketAdapterService;
  uploadedDocument: any;
  userId: number;
  userInfo: any;
  userList: any[] = [];
  userService: UserService;
  userSession: any[] = [];

  constructor(
    injector: Injector,
    private formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal
  ) {
    this.alertService = injector.get<AlertService>(AlertService);
    this.attachmentService = injector.get<AttachmentsService>(AttachmentsService);
    this.authService = injector.get<AuthService>(AuthService);
    this.chatService = injector.get<ChatService>(ChatService);
    this.httpService = injector.get<HttpService>(HttpService);
    this.localService = injector.get<LocalService>(LocalService);
    this.socketAdapter = injector.get<SocketAdapterService>(SocketAdapterService);
    this.userService = injector.get<UserService>(UserService);
    this.currentUser = JSON.parse(this.localService.getItem('userDetails'));
    this.userInfo = JSON.parse(this.localService.getItem('user'));
    this.userId = Number(this.localService.getItem('id'));
  }

  ngOnInit(): void {
    this.getUserList();
    this.getOnlineUsers();
    this.initializeChatForm();
    this.initializeChatSocket();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    this.onTypingSubs = fromEvent(this.onTyping.nativeElement, 'keyup')
      .pipe(
        /* prompt user when starts typing */
        tap(() => this.checkOnTyping(true)),
        debounceTime(1000)
      )
      .subscribe(() => {
        /* prompt user when stop typing */
        this.checkOnTyping(false); 
      });
  }

  initializeChatForm() {
    this.chatForm = this.formBuilder.group({
      sender_id: [this.userId],
      receiver_id: [],
      message: [],
      case_id: [],
      person_id: [],
      document_id: [],
      document_info: this.createDocumentFormGroup(),
      is_visible: [Boolean]
    });
  }

  createDocumentFormGroup() {
    return this.formBuilder.group({
      document_name: [],
      document_type: [],
      document_size: [],
      document_url: []
    });
  }

  initializeChatSocket() {
    /* get User Online/Offline Status */
    this.socketAdapter.onLine().subscribe(user => {
      this.onlineUsers = user.map(res => Number(res.userId));
    });

    /* get User Last Session Time */
    this.socketAdapter.getUserSession().subscribe(users => {
      this.userSession = [...users];
    });

    /* User Typing Prompt */
    this.socketAdapter.isTyping().subscribe((isReceiverTyping: boolean) => {
      this.isReceiverTyping = isReceiverTyping;
    });

    /* acknowledge after sending message to Sender */
    this.socketAdapter.privateMessageAcknowledge().subscribe(message => {
      console.log(message);
      if (message.messageData[0].id !== this.last_message_id && message.messageData[0].document_info?.document_url) {
        this.addAttachment(message.messageData[0].id);
        this.last_message_id = message.messageData[0].id;
      }
      this.getUserMessage();
    });

    /* Receive Message from Receiver */
    this.socketAdapter.recievePrivateMessage().subscribe(() => {
      this.getUserMessage();
    });

    /* After Receiver Reads Messages */
    this.socketAdapter.onReceiverReadsMessages().subscribe(() => {
      this.getUserMessage();
    });

    /* Get Unread Messages */
    this.socketAdapter.getUnreadMessages().subscribe(() => {
      this.getUserList();
    });
  }

  selectedNewUser(event) {
    const user = {
      id: event.id,
      receiver_id: event.id,
      fullname: event.fullname,
      abbrevation: this.chatService.getAbbrevation(event.fullname)
    };
    const checkExistingUser = this.userList.filter(chatUser => chatUser.id === +user.id);
    if (!checkExistingUser.length) {
      this.userList.unshift(user);
      this.filteredUserList = [...this.userList];
      this.chatService.addChatUsers(user).subscribe(
        _res => {
          console.log('ChatUsersAdded');
        },
        _error => this.alertService.error(AppConstants.errorMessage)
      );
    }
    this.onSelectUser(event.id, event.fullname, true);
    $('#newuserModal').modal('hide');
  }

  isOnline(id: any) {
    /* check if user is Online */
    return this.onlineUsers.includes(id); 
  }

  openUploadDocumentModal() {
    const uploadDoccumentModalRef = this.modalService.open(DocumentUploadComponent, { size: 'lg', backdrop: 'static' });
    uploadDoccumentModalRef.componentInstance.person_id = this.person_id;
    uploadDoccumentModalRef.componentInstance.case_id = this.case_id;
    uploadDoccumentModalRef.componentInstance.receiver_id = this.selectedUserId;
    uploadDoccumentModalRef.componentInstance.is_fileshare = this.is_fileshare;
    uploadDoccumentModalRef.componentInstance.is_fileupload = this.is_fileupload;
    uploadDoccumentModalRef.componentInstance.uploadedDocument.subscribe(document => {
      this.uploadedDocument = document;
      this.chatForm.get('document_info').patchValue({
        document_name: document.filename,
        document_type: document.file_type,
        document_size: document.document_size,
        document_url: document.documenturl
      });
      this.sendMessage();
    });
  }

  addAttachment(chatId: number) {
    if (this.is_fileupload) {
      this.uploadedDocument.chatid = chatId;
      this.chatService.addAttachment(this.uploadedDocument).subscribe(
        () => {
          console.log('Attachment Added successfully');
        },
        _error => this.alertService.error(AppConstants.errorMessage)
      );
    }
  }

  getUserList() {
    this.chatService.getChatUsers().subscribe(
      (res: any) => {
        this.userList = res.data.map(user => ({
          ...user,
          fullname: user.firstname + ' ' + user.lastname,
          abbrevation: (user.firstname + ' ' + user.lastname)
            .split(' ')
            .map(name => name.charAt(0).toLocaleUpperCase())
            .join('')
        }));
        this.filteredUserList = [...this.userList];
      },
      _error => this.alertService.error(AppConstants.errorMessage)
    );
  }

  getOnlineUsers() {
    this.chatService.getOnlineUsers().subscribe(
      res => {
        this.onlineUsers = res['data'].map(_res => Number(_res.id));
      },
      _error => this.alertService.error(AppConstants.errorMessage)
    );
  }

  onSelectUser(id: number, name: string, newUserStatus: boolean) {
    this.selectedUserId = +id;
    this.selectedUserName = name;
    this.selectedUserAbbrevation = this.chatService.getAbbrevation(name);
    this.getLastSession();
    this.chatForm.patchValue({ receiver_id: id });
    this.getUserMessage();
    !newUserStatus && this.socketAdapter.readMessages(this.selectedUserId, this.userId);
  }

  searchUser(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.filteredUserList = this.userList.filter(user => user?.fullname?.toLowerCase().startsWith(value.toLowerCase()));
  }

  getUserMessage() {
    const chatPayload = {
      sender_id: this.userId,
      receiver_id: Number(this.selectedUserId)
    };
    this.chatService.getUserMessage(chatPayload).subscribe(res => {
      this.selectedUserChat = res['data'].filter(
        message =>
          +message.sender_id === this.userId ||
          (+message.receiver_id === this.userId && !message.document_info.document_url)
      );
    });
  }

  getLastSession() {
    this.chatService.getUserSession(this.selectedUserId).subscribe(
      res => {
        this.userSession = res['data'];
      },
      _error => this.alertService.error(AppConstants.errorMessage)
    );
  }

  checkOnTyping(isTyping: boolean) {
    if (isTyping !== this.isTyping) {
      this.isTyping = isTyping;
      if (isTyping) {
        this.socketAdapter.onType({ sender_id: this.userId, receiver_id: this.selectedUserId, isTyping: true });
      } else {
        this.socketAdapter.onType({ sender_id: this.userId, receiver_id: this.selectedUserId, isTyping: false });
      }
    }
  }

  sendMessage() {
    this.chatForm.value.document_info.document_name
      ? this.chatForm.patchValue({ is_visible: false })
      : this.chatForm.patchValue({ is_visible: true });
    this.selectedUserChat.push({ ...this.chatForm.value, createdat: moment().format() });
    this.socketAdapter.sendPrivateMessage(this.chatForm.value);
    this.chatForm.controls['message'].reset();
    this.chatForm.get('document_info').reset();
  }

  resetChatWindow() {
    this.selectedUserId = 0;
    this.selectedUserName = '';
  }

  getMessageDate(messageIndex: number): string {
    
    const dateToday = moment().format(DATE_FORMAT);
    const dateYesterday = moment().subtract(1, 'day').format(DATE_FORMAT);
    const messageDate = moment(this.selectedUserChat[messageIndex].createdat).format(DATE_FORMAT);
    if (messageDate === dateToday) {
      return 'Today';
    } else if (messageDate === dateYesterday) {
      return 'Yesterday';
    } else {
      return messageDate;
    }
  }

  getUserSession(userId: number): string {
    const userSession = this.userSession?.filter(user => user.user_id === userId);
    if (userSession?.length) {
      const userLastSession = moment(userSession[0]?.clocked_out_at).format(DATE_FORMAT);
      const dateToday = moment().format(DATE_FORMAT);
      const dateYesterday = moment().subtract(1, 'day').format(DATE_FORMAT);

      if (userLastSession === dateToday) {
        return `Last Seen Today at ${moment(userSession[0]?.clocked_out_at).format('LT')}`;
      } else if (userLastSession === dateYesterday) {
        return `Last Seen Yesterday at ${moment(userSession[0]?.clocked_out_at).format('LT')}`;
      } else {
        return `Last Seen ${moment(userSession[0]?.clocked_out_at).format('LL')} at  ${moment(
          userSession[0]?.clocked_out_at
        ).format('LT')}`;
      }
    } else {
      return '';
    }
  }

  downloadFile(fileUrl: string, fileName: string) {
    this.chatService.getSignedDocumentUrl({ url: fileUrl }).subscribe(
      res => {
        if (res && res['data']) {
          saveAs(res['data'], fileName);
        }
      },
      _error => {
        this.alertService.error(AppConstants.errorMessage);
      }
    );
  }

  openDocumentStatusModal(status: string, document_id: string) {
    this.document_status = status;
    this.selectedDocumentId = document_id;
    this.chatService.getAttachmentById(document_id).subscribe(res => {
      this.document_title = res['data'][0].title;
      this.document_type = res['data'][0].documenttype;
      this.document_category = res['data'][0].documentcategory;
      this.documents_remarks = res['data'][0].approvalrejectremarks;
    });
  }

  closeDocumentStatusModal() {
    $('#documentRejectModal').modal('hide');
  }

  updateDocumentStatus() {
    const documentStatusPayload = {
      id: this.selectedDocumentId,
      title: this.document_title,
      documenttype: this.document_type,
      documentcategory: this.document_category,
      approvalrejectremarks: this.documents_remarks,
      approverejectby: String(this.userId),
      approverejectdt: moment().format(),
      status: this.document_status
    };
    this.chatService.updateDocumentStatus(documentStatusPayload).subscribe(
      () => {
        this.closeDocumentStatusModal();
        this.alertService.success(
          `Document ${this.document_status === 'Approve' ? 'Approved' : 'Rejected'} Successfully`
        );
        this.getUserMessage();
      },
      _error => this.alertService.error(AppConstants.errorMessage)
    );
  }
}
